import React, { useState, useContext } from 'react';
import {
  Box,
  IconButton,
  Container,
  Paper
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import PeopleIcon from '@mui/icons-material/People';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import DescriptionIcon from '@mui/icons-material/Description';
import Typography from '@mui/material/Typography';
import UnifiedAppBar from '../components/navigation/UnifiedAppBar';
import UnifiedSettingsDialog from './dialogs/UnifiedSettingsDialog';
import EventAggregatorApp from './EventAggregatorApp';
import PersonManagement from '../administration/PersonManagement';
import UnknownFacesList from '../administration/UnknownFacesList';
import WitnessStatementList from './statements/WitnessStatementList';
import { useSettings } from '../contexts/settingsContext';
import { CognitoUserContext } from '../contexts/cognitoUserContext';

/**
 * OperatorApp - Interface for operators
 * New functionality to be defined
 */
export default function OperatorApp() {
  const { cognitoUser } = useContext(CognitoUserContext);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [eventAggregatorOpen, setEventAggregatorOpen] = useState(false);
  const [personManagementOpen, setPersonManagementOpen] = useState(false);
  const [unknownFacesOpen, setUnknownFacesOpen] = useState(false);
  const [statementsOpen, setStatementsOpen] = useState(false);
  const settings = useSettings();

  // Build operator-specific left content
  const leftContent = (
    <>
      <IconButton
        color={eventAggregatorOpen ? "secondary" : "inherit"}
        onClick={() => {
          setEventAggregatorOpen(!eventAggregatorOpen);
          if (!eventAggregatorOpen) {
            setPersonManagementOpen(false);
            setUnknownFacesOpen(false);
            setStatementsOpen(false);
          }
        }}
        title="Event Aggregator"
      >
        <TimelineIcon />
      </IconButton>
      <IconButton
        color={personManagementOpen ? "secondary" : "inherit"}
        onClick={() => {
          setPersonManagementOpen(!personManagementOpen);
          if (!personManagementOpen) {
            setEventAggregatorOpen(false);
            setUnknownFacesOpen(false);
            setStatementsOpen(false);
          }
        }}
        title="Person Management"
      >
        <PeopleIcon />
      </IconButton>
      <IconButton
        color={unknownFacesOpen ? "secondary" : "inherit"}
        onClick={() => {
          setUnknownFacesOpen(!unknownFacesOpen);
          if (!unknownFacesOpen) {
            setEventAggregatorOpen(false);
            setPersonManagementOpen(false);
            setStatementsOpen(false);
          }
        }}
        title="Unknown Faces / SOI"
      >
        <PersonSearchIcon />
      </IconButton>
      <IconButton
        color={statementsOpen ? "secondary" : "inherit"}
        onClick={() => {
          setStatementsOpen(!statementsOpen);
          if (!statementsOpen) {
            setEventAggregatorOpen(false);
            setPersonManagementOpen(false);
            setUnknownFacesOpen(false);
          }
        }}
        title="Witness Statements"
      >
        <DescriptionIcon />
      </IconButton>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Unified App Bar */}
      <UnifiedAppBar
        mode="operator"
        leftContent={leftContent}
        title="Operator Panel"
        onSettingsOpen={() => setSettingsOpen(true)}
        cognitoUser={cognitoUser}
      />

      {/* Main Content Area */}
      <Box sx={{ mt: 1, mb: 1, mx: 1, flexGrow: 1, overflow: 'auto' }}>
        {/* Keep EventAggregatorApp mounted to preserve WebSocket connection */}
        <Box sx={{ display: eventAggregatorOpen ? 'block' : 'none', height: '100%' }}>
          <EventAggregatorApp />
        </Box>

        {personManagementOpen && (
          <PersonManagement />
        )}

        {unknownFacesOpen && (
          <UnknownFacesList />
        )}

        {statementsOpen && (
          <WitnessStatementList />
        )}

        {!eventAggregatorOpen && !personManagementOpen && !unknownFacesOpen && !statementsOpen && (
          <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Operator Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Click the Event Aggregator, Person Management, Unknown Faces, or Witness Statements icon to get started.
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>

      {/* Settings Dialog */}
      <UnifiedSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        cognitoUser={cognitoUser}
        deleteConfirmation={settings.deleteConfirmation}
        onDeleteConfirmationChange={settings.handleDeleteConfirmationChange}
        autoEnableVoice={settings.autoEnableVoice}
        onAutoEnableVoiceChange={settings.setAutoEnableVoice}
        stealthMode={settings.stealthMode}
        onStealthModeChange={settings.setStealthMode}
        visibleEventTypes={settings.visibleEventTypes}
        onVisibleEventTypesChange={settings.updateVisibleEventTypes}
        removeBg={settings.removeBg}
        setRemoveBg={settings.setRemoveBg}
        sliderValue={settings.sliderValue}
        setSliderValue={settings.setSliderValue}
        multiFaceMode={settings.multiFaceMode}
        setMultiFaceMode={settings.setMultiFaceMode}
        showBoundingBoxes={settings.showBoundingBoxes}
        setShowBoundingBoxes={settings.setShowBoundingBoxes}
        immediateFaceMatch={settings.immediateFaceMatch}
        setImmediateFaceMatch={settings.setImmediateFaceMatch}
        isIndoor={settings.isIndoor}
        setIsIndoor={settings.setIsIndoor}
        updateCharacteristicsOnMatch={settings.updateCharacteristicsOnMatch}
        setUpdateCharacteristicsOnMatch={settings.setUpdateCharacteristicsOnMatch}
        locationHighAccuracy={settings.locationHighAccuracy}
        setLocationHighAccuracy={settings.setLocationHighAccuracy}
        locationAcquisitionTimeout={settings.locationAcquisitionTimeout}
        setLocationAcquisitionTimeout={settings.setLocationAcquisitionTimeout}
        locationCacheMaxAge={settings.locationCacheMaxAge}
        setLocationCacheMaxAge={settings.setLocationCacheMaxAge}
        compressionQuality={settings.compressionQuality}
        setCompressionQuality={settings.setCompressionQuality}
        autoAdjustCompression={settings.autoAdjustCompression}
        setAutoAdjustCompression={settings.setAutoAdjustCompression}
        autoCreateEventOnAIMatch={settings.autoCreateEventOnAIMatch}
        setAutoCreateEventOnAIMatch={settings.setAutoCreateEventOnAIMatch}
        aggregatorEventLimit={settings.aggregatorEventLimit}
        setAggregatorEventLimit={settings.setAggregatorEventLimit}
      />
    </Box>
  );
}
